import { useEffect, useRef, useState } from 'react';

import {
  TUseScrollFadeOptions,
  TUseScrollFadeResult,
} from '@/shared/components/ui/scroll-fade/use-scroll-fade/use-scroll-fade.types';

export const useScrollFade = <T extends HTMLElement = HTMLDivElement>({
  enabled = true,
  axis = 'vertical',
}: TUseScrollFadeOptions = {}): TUseScrollFadeResult<T> => {
  const ref = useRef<T>(null);
  const [fade, setFade] = useState({ start: false, end: false });

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) {
      setFade((prev) =>
        !prev.start && !prev.end ? prev : { start: false, end: false },
      );
      return;
    }

    const update = () => {
      const isHorizontal = axis === 'horizontal';
      const offset = isHorizontal ? element.scrollLeft : element.scrollTop;
      const scrollSize = isHorizontal
        ? element.scrollWidth
        : element.scrollHeight;
      const visibleSize = isHorizontal
        ? element.clientWidth
        : element.clientHeight;

      const start = offset > 0;
      const end = Math.ceil(offset + visibleSize) < scrollSize;
      setFade((prev) =>
        prev.start === start && prev.end === end ? prev : { start, end },
      );
    };

    update();
    element.addEventListener('scroll', update, { passive: true });
    // Content grows async (loaded fields/targets) without resizing the
    // viewport, so watch subtree mutations alongside size changes.
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(element, { childList: true, subtree: true });

    return () => {
      element.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [enabled, axis]);

  return { ref, fadeStart: fade.start, fadeEnd: fade.end };
};
